import { useQuery } from '@tanstack/react-query'
import { createColumnHelper } from '@tanstack/react-table'
import to from 'await-to-js'
import { ActionButtons } from 'components/Buttons/ActionButtons'
import { DataTable, DataTableProvider } from 'components/DataTable'
import dialogConfirm from 'components/dialogConfirm'
import { OverflownTextTooltip } from 'components/OverflownTextTooltip'
import { Tooltip } from 'components/Tooltip/tooltip'
import { DateCell } from 'components/Tables/CommonTableCells/DateCell'
import { useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import apiService from 'services/api'
import { featureFlagService } from 'utils/featureFlagService'
import { ConnectionsTable } from './ConnectionsTable'
import { ColumnsStoreProvider } from 'stores/ColumnsStore/ColumnsStoreProvider'
import { getTableQueryKey } from 'utils/getTableQueryKey'
import { dataTableSortingStoreRepo } from '../../../components/DataTable/DataTableSorting/DataTableSortingStore'
import { DataTableContainer } from '../../../components/DataTable/UI'
import FbButton from '../../../components/FbUI/FbButton'
import { GoogleButton, OutlookButton } from './ProviderButtons'
import { Modal } from '../../../components/UI/Modal/Modal'
import * as S from './styles'
import { EmailIntegration } from 'models/emailIntegration'
import { FiRefreshCw, FiKey, FiCheckCircle } from 'react-icons/fi'
import { CiWarning } from 'react-icons/ci'
import { Input } from 'components/FormUtilsV2/Input/input'
import CustomCheckbox from 'components/Checkbox/checkbox'

const PAGE_SIZE = 25

const sortableFields = ['email', 'modified', 'created']

const tableKey = 'email-integration-table'
export function EmailIntegrationTableComponent() {
  const { enableCalendarIntegration } = featureFlagService()

  // FF ON: render the grants-based connections table
  if (enableCalendarIntegration) {
    return <ConnectionsTable />
  }

  // FF OFF: render the original email-only table (unchanged below)
  return <EmailOnlyTable />
}

function EmailOnlyTable() {
  const api = apiService()
  const [showModal, setShowModal] = useState<boolean>(false)

  const [selectedEmailID, setSelectedEmailID] = useState<number>()
  const [showSuccessModal, setShowSuccessModal] = useState<boolean>(false)
  const [isShared, setIsShared] = useState<boolean>(true)
  const [email, setEmail] = useState<string>('')
  const { sorting } = dataTableSortingStoreRepo.getStore(tableKey)()

  const sortParams = useMemo(() => {
    const params: Record<string, string> = {}

    if (sorting?.length) {
      params['sort'] = sorting[0].desc ? '-' + sorting[0]?.id : sorting[0]?.id
    }

    return params
  }, [sorting])

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const success = urlParams.get('success')
    const error = urlParams.get('error')
    if (success) {
      setShowSuccessModal(true)
      // Remove the success param from the URL
      urlParams.delete('success')
      window.history.replaceState({}, '', `${window.location.pathname}`)
    } else if (error) {
      // Handle error case
      const message = urlParams.get('message')
      if (error === 'insufficient_permissions') {
        toast.error(
          message ||
            'You must allow all requested permissions to integrate your email'
        )
      } else {
        toast.error(
          'There was an issue connecting to your email. Make sure you grant the necessary permissions and try again.'
        )
      }
      // Remove the error params from the URL
      urlParams.delete('error')
      urlParams.delete('message')
      window.history.replaceState({}, '', `${window.location.pathname}`)
    }
  }, [])

  function openModal() {
    setShowModal(true)
  }

  function hideModal() {
    setShowModal(false)
    setSelectedEmailID(undefined)
  }

  const [page, setPage] = useState(1)

  const TABLE_QUERY_KEY = getTableQueryKey({
    tableKey,
    filterParams: sortParams,
    page: page,
  })

  // DATA FETCHING
  const { isFetching, data, refetch } = useQuery({
    queryKey: TABLE_QUERY_KEY,
    queryFn: async ({ signal }) => {
      const [err, data] = await to(
        api.emailIntegration.getEmailIntegrationList(
          {
            ...sortParams,
            limit: PAGE_SIZE,
            page: page,
          },
          signal
        )
      )

      if (err) {
        throw err
      }

      return data
    },
  })

  const onContinueButtonClick = async (provider: string) => {
    const [err, response] = await to(
      api.emailIntegration.getEmailIntegrationAuthUrl({
        provider,
        isShared,
        features: ['email'],
      })
    )
    if (err) {
      toast.error('Error creating email')
      return
    }

    if (response?.auth_url) {
      // Redirect to the auth url
      window.location.href = response.auth_url
    }

    toast.success('Email integration requested')
    await refetch()
    hideModal()
  }

  const onSubmitUpdate = async () => {
    if (!selectedEmailID) return

    const [err] = await to(
      api.emailIntegration.updateEmailIntegration(selectedEmailID, {
        is_shared: isShared,
      })
    )
    if (err) {
      toast.error('Error updating email')
      return
    }

    toast.success('Email updated')
    await refetch()
    hideModal()
  }

  async function handleRowDelete(index: number) {
    const id = data?.results?.[index]?.id
    if (!id) return

    if (
      await dialogConfirm(
        'Removing this integration will delete any record of existing emails sent or received by this address, and prevent any future inbound or outbound mail from syncing to First Bite.',
        'Remove Email Integration from First Bite?'
      )
    ) {
      const [err] = await to(api.emailIntegration.deleteEmailIntegration(id))
      if (err) {
        toast.error('Error deleting email')
        return
      }
      toast.success('Email deleted')
      await refetch()
    }
  }

  function handleRowEdit(index: number) {
    setShowModal(true)
    const email = data?.results?.[index]
    if (email) {
      setSelectedEmailID(email.id)
      setIsShared(email.is_shared)
      setEmail(email.email)
    }
  }

  async function syncEmailIntegration(id: number) {
    const [err] = await to(api.emailIntegration.syncEmailIntegration(id))
    if (err) {
      toast.error('Error syncing email')
      return
    }
    toast.info(
      'Started syncing emails for this account. You will be notified when it is done.'
    )
    await refetch()
  }

  async function reauthenticateEmailIntegration(integration: EmailIntegration) {
    if (!integration.provider) {
      toast.error(
        'Unable to reauthenticate: provider information not available'
      )
      return
    }

    const [err, response] = await to(
      api.emailIntegration.getEmailIntegrationAuthUrl({
        provider: integration.provider,
        isShared: integration.is_shared,
        email: integration.email,
        features: ['email'],
      })
    )
    if (err) {
      toast.error('Error creating reauthentication URL')
      return
    }

    if (response?.auth_url) {
      // Redirect to the auth url
      window.location.href = response.auth_url
    }
  }

  const columnHelper = createColumnHelper<EmailIntegration>()

  const descriptionText = `Email integrations allow you to send, receive, and view emails within First Bite. When you add an account, only emails to and from contacts already in First Bite will be synced.`

  return (
    <DataTableContainer>
      <DataTable
        tableHeader={
          <S.SettingsTableHeader>
            <S.TableDescriptionText>
              <OverflownTextTooltip
                tooltipText={descriptionText}
                maxLines={4}
                placement="bottom"
              >
                {descriptionText}
              </OverflownTextTooltip>
            </S.TableDescriptionText>
            <FbButton onClick={() => openModal()}>Add new email</FbButton>
          </S.SettingsTableHeader>
        }
        tableKey={tableKey}
        data={data?.results ?? []}
        columns={[
          columnHelper.accessor('email', {
            header: 'Email',
            size: 200,
          }),
          columnHelper.accessor('is_shared', {
            header: 'Shared',
            size: 150,
            cell: ({ row }) => {
              return row.original.is_shared ? 'Yes' : 'No'
            },
          }),
          columnHelper.accessor('status', {
            header: 'Status',
            size: 100,
            cell: (info) => {
              const value = info.getValue()
              const valueMap = {
                synced: 'Synced',
                syncing: 'Syncing',
                not_synced: 'Not Synced',
              }

              return (
                <span
                  className={`${
                    value === 'synced'
                      ? 'text-green-500'
                      : value === 'syncing'
                        ? 'text-yellow-500'
                        : 'text-red-500'
                  }`}
                >
                  {valueMap[value as keyof typeof valueMap]}
                </span>
              )
            },
          }),
          columnHelper.accessor('is_expired', {
            header: 'Connection Status',
            size: 120,
            cell: (info) => {
              const isExpired = info.getValue()
              const integration = info.row.original

              if (isExpired) {
                return (
                  <Tooltip
                    title="Connection broken"
                    content="Click to reauthenticate your email account"
                    placement="top"
                  >
                    <div
                      onClick={() =>
                        reauthenticateEmailIntegration(integration)
                      }
                      className="cursor-pointer flex items-center text-red-500"
                    >
                      <CiWarning size={16} className="mr-1" />
                      <span>Disconnected</span>
                    </div>
                  </Tooltip>
                )
              }

              return (
                <div className="flex items-center text-green-500">
                  <FiCheckCircle size={16} className="mr-1" />
                  <span>Connected</span>
                </div>
              )
            },
          }),
          columnHelper.accessor('modified', {
            header: 'Modified',
            size: 100,
            cell: DateCell,
          }),
          columnHelper.accessor('created', {
            header: 'Created',
            size: 100,
            cell: DateCell,
          }),
          columnHelper.display({
            id: 'edit',
            header: 'Actions',
            meta: { rightAlign: true },
            cell: ({ row }) => {
              return (
                <div className="flex justify-end">
                  <FiKey
                    size={20}
                    color="#667085"
                    className="cursor-pointer mr-3 justify-end"
                    title="Reauthenticate"
                    onClick={() => reauthenticateEmailIntegration(row.original)}
                  />
                  <FiRefreshCw
                    size={20}
                    color="#667085"
                    className="cursor-pointer mr-3 justify-end"
                    title="Sync emails"
                    onClick={() => syncEmailIntegration(row.original.id)}
                  />
                  <div>
                    <ActionButtons
                      className="justify-end pr-0"
                      onDelete={() => handleRowDelete(row.index)}
                      onEdit={() => handleRowEdit(row.index)}
                    />
                  </div>
                </div>
              )
            },
            size: 100,
          }),
        ]}
        sortableFields={sortableFields}
        isPaginationEnabled={true}
        paginationOptions={{
          totalRows: data?.count ?? 0,
          pageSize: PAGE_SIZE,
          setPage: setPage,
          page: page,
          isPaginationLoading: isFetching,
        }}
        loading={isFetching}
      />
      <Modal
        open={showModal}
        title={selectedEmailID ? 'Edit Email Visibility' : 'Add Email'}
        description={
          selectedEmailID
            ? ''
            : 'Select your email provider to add an account to First Bite, then choose between private or team visibility. You can always change visibility preferences later.'
        }
        onOpenChange={(open) => !open && hideModal()}
        onAccept={selectedEmailID ? onSubmitUpdate : undefined}
        acceptButtonText={selectedEmailID ? 'Update' : 'Create'}
        hideCancelButton={!selectedEmailID}
      >
        {selectedEmailID ? (
          <Input
            name="email"
            type="text"
            placeholder="Email"
            disabled={true}
            value={email}
          />
        ) : (
          <div className="flex flex-col gap-2 items-center mb-4">
            <GoogleButton
              className="w-min"
              onClick={() => onContinueButtonClick('google')}
            />
            <OutlookButton
              className="w-min"
              onClick={() => onContinueButtonClick('microsoft')}
            />
          </div>
        )}

        <div className="flex items-center">
          <CustomCheckbox
            id="is_shared"
            onChange={(e) => setIsShared(e.target.checked)}
            value={isShared ? 'true' : 'false'}
            checked={isShared ?? true}
          />
          <label htmlFor="is_shared" className="ml-2">
            Share email visibility with my team
          </label>
        </div>
      </Modal>
      <Modal
        open={showSuccessModal}
        title={'Success'}
        description="Email integrated successfully"
        onOpenChange={(open) => !open && setShowSuccessModal(false)}
        onAccept={() => setShowSuccessModal(false)}
        acceptButtonText={'Close'}
        hideCancelButton
      />
    </DataTableContainer>
  )
}

export function EmailIntegrationTable() {
  return (
    <ColumnsStoreProvider tableKey={tableKey}>
      <DataTableProvider tableKey={tableKey}>
        <EmailIntegrationTableComponent />
      </DataTableProvider>
    </ColumnsStoreProvider>
  )
}
